// Immediate Reset library — states + guided interventions (1–5 min).
// `free: true` interventions are always available; the rest are Premium.
// No shame language. No clinical claims. Steps may carry optional `seconds` timers.

export const RESET_STATES = [
  { id: "anxious", label: "Anxious", sub: "racing thoughts, tight chest" },
  { id: "angry", label: "Angry", sub: "heat rising, urge to react" },
  { id: "overwhelmed", label: "Overwhelmed", sub: "too much at once" },
  { id: "impulsive", label: "About to act on impulse", sub: "a decision pushing at you" },
  { id: "craving", label: "Craving or urge", sub: "a pull toward a habit" },
  { id: "spiraling", label: "Spiraling", sub: "thoughts feeding on themselves" },
  { id: "restless", label: "Can't settle down", sub: "wired, restless, can't sleep" },
  { id: "reset", label: "Just need a reset", sub: "a few quiet minutes" },
];

// Maps onboarding focus areas → reset states, for ordering the picker.
export const FOCUS_TO_STATES = {
  anxiety: ["anxious", "spiraling"],
  anger: ["angry"],
  overwhelm: ["overwhelmed"],
  impulsive: ["impulsive"],
  cravings: ["craving"],
  emotions: ["spiraling", "overwhelmed"],
  sleep: ["restless"],
  habit: ["craving", "impulsive"],
};

export const RESET_INTERVENTIONS = [
  // ---------- FREE (essential set) ----------
  {
    id: "ri_box_breath",
    title: "Box breathing",
    tagline: "Slow the body first. The mind follows.",
    minutes: 2,
    free: true,
    category: "breathing",
    states: ["anxious", "angry", "overwhelmed", "spiraling", "restless", "reset"],
    steps: [
      { text: "Sit or stand somewhere you can be still for two minutes. Let your shoulders drop." },
      { text: "Breathe in slowly through your nose for 4 counts.", seconds: 20 },
      { text: "Now settle into the rhythm: in for 4, hold for 4, out for 4, hold for 4. Keep going.", seconds: 60 },
      { text: "Last round. Let the exhale be a little longer this time.", seconds: 20 },
    ],
    closing: "Your body had a little room to slow down.",
  },
  {
    id: "ri_54321",
    title: "5-4-3-2-1 grounding",
    tagline: "Come back to the room you're actually in.",
    minutes: 3,
    free: true,
    category: "grounding",
    states: ["anxious", "spiraling", "overwhelmed", "reset"],
    steps: [
      { text: "Look around. Name 5 things you can see. Say them slowly — out loud if you can.", seconds: 30 },
      { text: "Notice 4 things you can physically feel. Your feet on the floor. The fabric on your skin.", seconds: 30 },
      { text: "Listen for 3 sounds. Near or far. Just notice them.", seconds: 25 },
      { text: "Find 2 things you can smell — or 2 smells you like remembering.", seconds: 20 },
      { text: "Name 1 thing you can taste, or 1 sip of something nearby.", seconds: 15 },
    ],
    closing: "You're here. Notice whether the moment feels any different.",
  },
  {
    id: "ri_delay",
    title: "The 10-minute delay",
    tagline: "You don't have to refuse the urge. Just delay it.",
    minutes: 10,
    free: true,
    category: "urge",
    states: ["impulsive", "craving", "angry"],
    steps: [
      { text: "You don't have to decide anything right now. The only job is: not yet." },
      { text: "Set this moment down for 10 minutes. Move to a different room or step outside if you can.", seconds: 600 },
      { text: "The delay is over. Check in: is the pull the same, weaker, stronger, or simply different?" },
    ],
    closing: "You gave yourself time before choosing. Notice what changed, if anything.",
  },
  {
    id: "ri_physical",
    title: "Physical reset",
    tagline: "Change the body's channel in 90 seconds.",
    minutes: 2,
    free: true,
    category: "body",
    states: ["angry", "restless", "craving", "reset"],
    steps: [
      { text: "Stand up. Shake out your hands and arms like you're flicking water off them.", seconds: 15 },
      { text: "Do 10 slow shoulder rolls, or press your palms together hard for 10 seconds and release.", seconds: 25 },
      { text: "Splash cold water on your face or hold something cold. If you can't — press your feet firmly into the floor.", seconds: 30 },
      { text: "Take 3 slow breaths, exhaling longer than you inhale.", seconds: 20 },
    ],
    closing: "You gave the energy somewhere else to go for a moment.",
  },
  {
    id: "ri_name_it",
    title: "Name it to tame it",
    tagline: "Put a few words around what is here.",
    minutes: 2,
    free: true,
    category: "thought",
    states: ["spiraling", "anxious", "overwhelmed", "angry"],
    steps: [
      { text: "Finish this sentence, silently or out loud: \"Right now I'm feeling…\" Use the most honest word you have." },
      { text: "Now add: \"…and that makes sense, because…\" You do not need a complete explanation.", seconds: 30 },
      { text: "Last: \"This is a feeling. It's strong, and it will pass.\" Say it once, slowly." },
    ],
    closing: "You named what was present. You can leave it there for now.",
  },
  {
    id: "ri_decision_pause",
    title: "Decision pause",
    tagline: "For the message, purchase, or move you might regret.",
    minutes: 3,
    free: true,
    category: "pause",
    states: ["impulsive", "angry"],
    steps: [
      { text: "Don't send, buy, say, or do it yet. Nothing is lost by waiting three minutes." },
      { text: "Ask: \"Will tomorrow-me thank me for this?\" Sit with the honest answer.", seconds: 45 },
      { text: "Ask: \"What would I tell a friend about to do this?\"", seconds: 45 },
      { text: "Now choose when you are ready — including choosing to wait longer." },
    ],
    closing: "You made a little room between the impulse and the act.",
  },
  {
    id: "ri_settle",
    title: "Wind-down breath",
    tagline: "For the night your body won't switch off.",
    minutes: 4,
    free: true,
    category: "breathing",
    states: ["restless", "anxious", "reset"],
    steps: [
      { text: "Dim what you can. Lie down or lean back. Let your jaw unclench." },
      { text: "Breathe in for 4… and out for 6. Keep the exhale a little longer and notice how your body responds.", seconds: 90 },
      { text: "Now scan downward: forehead… jaw… shoulders… hands… let each one soften as you pass it.", seconds: 60 },
      { text: "Stay with the slow exhale a little longer. Nothing to solve tonight.", seconds: 45 },
    ],
    closing: "You gave your body some room to settle.",
  },

  // ---------- PREMIUM (full library) ----------
  {
    id: "ri_478",
    title: "4-7-8 breathing",
    tagline: "Another way to slow things down and make room to choose.",
    minutes: 3,
    free: false,
    category: "breathing",
    states: ["anxious", "restless", "spiraling"],
    steps: [
      { text: "Rest the tip of your tongue behind your top front teeth. Exhale fully through your mouth." },
      { text: "In through the nose for 4… hold for 7… out through the mouth for 8. Repeat 4 rounds.", seconds: 90 },
      { text: "Return to normal breathing. Notice the difference in your chest and hands.", seconds: 20 },
    ],
    closing: "Notice what the slower breathing changed, if anything.",
  },
  {
    id: "ri_urge_surf",
    title: "Urge surfing",
    tagline: "Ride the wave instead of fighting it.",
    minutes: 5,
    free: false,
    category: "urge",
    states: ["craving", "impulsive"],
    steps: [
      { text: "Find the urge in your body. Chest? Stomach? Hands? Just locate it — no judgment." },
      { text: "Describe it like a scientist: tight, warm, buzzing, heavy? Watch it without acting.", seconds: 60 },
      { text: "Urges behave like waves — they rise, they crest, they fall. Breathe slowly and watch this one crest.", seconds: 120 },
      { text: "Notice: has it shifted at all? It may feel smaller, stronger, or simply different.", seconds: 30 },
    ],
    closing: "The urge may have shifted, stayed, or changed shape. You noticed it without rushing.",
  },
  {
    id: "ri_tape_forward",
    title: "Play the tape forward",
    tagline: "See past the first five minutes.",
    minutes: 3,
    free: false,
    category: "urge",
    states: ["craving", "impulsive"],
    steps: [
      { text: "Picture doing the thing you're pulled toward. Be honest about the first few minutes — they might feel good." },
      { text: "Now keep the tape rolling. An hour later — how do you feel? Tonight? Tomorrow morning?", seconds: 60 },
      { text: "Now play a second tape: you don't do it. Fast-forward to tomorrow morning on that path.", seconds: 45 },
      { text: "You've seen both endings. Choose with the whole tape in view." },
    ],
    closing: "You considered more than the first few minutes.",
  },
  {
    id: "ri_muscle_release",
    title: "Tense & release",
    tagline: "Let the body discharge what it's holding.",
    minutes: 4,
    free: false,
    category: "body",
    states: ["angry", "anxious", "restless"],
    steps: [
      { text: "Make fists and squeeze hard for 5 seconds… then release completely. Feel the difference.", seconds: 20 },
      { text: "Tense your shoulders up to your ears… hold… drop them.", seconds: 20 },
      { text: "Press your feet into the floor and tense your legs… hold… release.", seconds: 20 },
      { text: "Squeeze everything at once — fists, jaw, shoulders, legs… hold for 5… and let it all go.", seconds: 25 },
      { text: "Breathe slowly. Scan for what softened.", seconds: 30 },
    ],
    closing: "Notice where your body feels different after releasing tension.",
  },
  {
    id: "ri_cold_reset",
    title: "Cold reset",
    tagline: "Use a strong physical sensation to interrupt the moment.",
    minutes: 2,
    free: false,
    category: "body",
    states: ["angry", "spiraling", "impulsive"],
    steps: [
      { text: "Get to cold water — a tap, a shower, or something cold from the freezer wrapped in cloth." },
      { text: "Hold cold water on your face, wrists, or the back of your neck for 30 seconds. Breathe through it.", seconds: 30 },
      { text: "Repeat once more if you need it. Then take 3 slow breaths.", seconds: 30 },
    ],
    closing: "Notice whether the intensity changed after the cold sensation.",
  },
  {
    id: "ri_opposite",
    title: "Opposite action",
    tagline: "Do one small thing the feeling wouldn't choose.",
    minutes: 3,
    free: false,
    category: "thought",
    states: ["angry", "spiraling", "overwhelmed"],
    steps: [
      { text: "Name what the feeling wants you to do — withdraw, lash out, give up, scroll, numb." },
      { text: "Pick the smallest opposite: unclench, soften your voice, stand up, open a window, text something kind.", seconds: 30 },
      { text: "Do it now — even at 50% effort. Acting opposite tells the feeling it isn't in charge.", seconds: 60 },
    ],
    closing: "You tried one action that did not have to follow the feeling.",
  },
  {
    id: "ri_worry_container",
    title: "The worry container",
    tagline: "Park it — don't carry it.",
    minutes: 3,
    free: false,
    category: "thought",
    states: ["anxious", "spiraling", "restless"],
    steps: [
      { text: "Name the worry in one sentence. Just one." },
      { text: "Ask: \"Can I do anything about this in the next hour?\" If yes — name the single smallest step.", seconds: 30 },
      { text: "If no — imagine placing it in a container with a lid. It will keep. You can come back tomorrow.", seconds: 30 },
      { text: "Every time it knocks tonight, remind it: \"You're in the container. I'll see you tomorrow.\"" },
    ],
    closing: "You gave the worry somewhere to wait for now.",
  },
  {
    id: "ri_body_scan",
    title: "Two-minute body scan",
    tagline: "Attention is the anchor.",
    minutes: 3,
    free: false,
    category: "grounding",
    states: ["restless", "anxious", "reset"],
    steps: [
      { text: "Close your eyes or soften your gaze. Take one slow breath." },
      { text: "Bring attention to the top of your head… slowly move it down: face… neck… shoulders…", seconds: 40 },
      { text: "Continue: chest… stomach… hips… legs… feet. Don't fix anything. Just visit.", seconds: 50 },
      { text: "Rest attention in your feet for a few breaths. Feel the ground carrying you.", seconds: 25 },
    ],
    closing: "Your attention had somewhere else to land for a moment.",
  },
  {
    id: "ri_anchor_3",
    title: "Three good anchors",
    tagline: "Not gratitude homework — just three true things.",
    minutes: 2,
    free: false,
    category: "reflect",
    states: ["overwhelmed", "spiraling", "reset"],
    steps: [
      { text: "Name one thing that is okay right now, however small. For example: \"I have coffee.\"" },
      { text: "Name one person or creature you're glad exists.", seconds: 20 },
      { text: "Name one thing you handled recently — even imperfectly.", seconds: 20 },
    ],
    closing: "You named three things that are here with you right now.",
  },
  {
    id: "ri_values_check",
    title: "Values check",
    tagline: "When the next step is unclear, aim at who you want to be.",
    minutes: 3,
    free: false,
    category: "reflect",
    states: ["impulsive", "overwhelmed", "reset"],
    steps: [
      { text: "Ask: \"What kind of person do I want to be in this exact situation?\" One word is enough — steady, kind, honest." },
      { text: "Ask: \"What would that person do in the next ten minutes?\"", seconds: 45 },
      { text: "Do the first 10% of that. Just the first 10%." },
    ],
    closing: "You made room to consider what matters to you before acting.",
  },
  {
    id: "ri_anger_letter",
    title: "The unsent message",
    tagline: "Say everything — send nothing.",
    minutes: 5,
    free: false,
    category: "pause",
    states: ["angry", "impulsive"],
    steps: [
      { text: "You have things to say. Say them here first — where no one gets hurt." },
      { text: "Write or speak the message at full honesty. Don't edit. Don't soften.", seconds: 120 },
      { text: "Now read it as the receiver. What lands? What would you change?", seconds: 45 },
      { text: "Hold it overnight. If it's still true and useful in the morning, you can send a version that still feels true. Drafts on Hold can keep it for you." },
    ],
    closing: "The feeling had somewhere private to go before you decide what to send.",
  },
  {
    id: "ri_task_triage",
    title: "One-thing triage",
    tagline: "For when everything feels urgent at once.",
    minutes: 3,
    free: false,
    category: "thought",
    states: ["overwhelmed"],
    steps: [
      { text: "Everything cannot be first. Accept that for the next three minutes." },
      { text: "Of everything on your plate — what is the one thing that matters most today? Not this week. Today.", seconds: 45 },
      { text: "What is the smallest first move on that one thing? Under five minutes.", seconds: 30 },
      { text: "Everything else officially waits. You are allowed to do one thing at a time." },
    ],
    closing: "You narrowed the pile to one possible next move.",
  },
  {
    id: "ri_half_smile",
    title: "Soften the face, soften the mind",
    tagline: "The body talks back to the brain.",
    minutes: 2,
    free: false,
    category: "body",
    states: ["angry", "spiraling", "reset"],
    steps: [
      { text: "Unclench your jaw. Let your tongue drop from the roof of your mouth." },
      { text: "Let the corners of your mouth lift — barely. Not a fake smile. Just… less armor.", seconds: 30 },
      { text: "Drop your shoulders. Open your hands, palms up. Breathe slowly for 5 breaths.", seconds: 40 },
    ],
    closing: "Notice whether softening your face and hands changed anything.",
  },
  {
    id: "ri_sound_anchor",
    title: "Sound anchoring",
    tagline: "Let hearing do the grounding.",
    minutes: 2,
    free: false,
    category: "grounding",
    states: ["anxious", "restless", "reset"],
    steps: [
      { text: "Close your eyes. Find the farthest sound you can hear. Stay with it.", seconds: 30 },
      { text: "Now the closest sound — maybe your own breath. Stay with it.", seconds: 30 },
      { text: "Hold both at once, far and near. You are the quiet point between them.", seconds: 30 },
    ],
    closing: "You widened your attention beyond the thought for a moment.",
  },
  {
    id: "ri_future_self",
    title: "A line to tomorrow-you",
    tagline: "Borrow perspective from the person you'll be.",
    minutes: 3,
    free: false,
    category: "reflect",
    states: ["craving", "impulsive", "spiraling"],
    steps: [
      { text: "Picture yourself tomorrow morning — coffee, first light, this moment behind you." },
      { text: "What does that version of you want to say to you right now? Listen honestly.", seconds: 45 },
      { text: "If it helps, write it as a short letter in Tomorrow-you and send it forward in time." },
    ],
    closing: "You gave tomorrow-you a chance to add perspective.",
  },
];

export const getInterventionById = (id) =>
  RESET_INTERVENTIONS.find((r) => r.id === id) || null;

export const getInterventionsForState = (stateId) => {
  const matched = RESET_INTERVENTIONS.filter((r) => r.states.includes(stateId));
  // Free first, then by shortest.
  return matched.sort((a, b) => (b.free - a.free) || (a.minutes - b.minutes));
};

export const FREE_INTERVENTION_COUNT = RESET_INTERVENTIONS.filter((r) => r.free).length;
export const TOTAL_INTERVENTION_COUNT = RESET_INTERVENTIONS.length;
